// Sprint lifecycle helpers (TPT9102): which phase a sprint is in and what Sprint Planning's
// completion dialog says about it (pages/sprint-planning.js). Pure (no DOM, no api.js) so node:test
// can import it without stubs — same shape as lib/sprint-dates.js. A sprint row's status alone can't
// tell the phases apart: 'active' is every sprint that isn't closed, started or not.

import { todayIso, shortDay } from './due-tasks.js';

// 'closed', 'running' (started and open) or 'planned' (open, never started).
export function sprintPhase(sprint) {
  if (sprint.status === 'closed') return 'closed';
  return sprint.started_at ? 'running' : 'planned';
}

// A lifecycle timestamp as the viewer's short calendar day ("Oct 6"), or '' without a usable one.
function dayOf(value) {
  const date = value ? new Date(value) : null;
  return date && Number.isFinite(date.getTime()) ? shortDay(todayIso(date)) : '';
}

// "Started Oct 6", "Closed Oct 19", "Not started". A closed sprint without a completion stamp, or
// a running one whose stamp can't be read, drops the day rather than show a blank one.
export function phaseLabel(sprint) {
  const phase = sprintPhase(sprint);
  if (phase === 'planned') return 'Not started';
  const day = dayOf(phase === 'closed' ? sprint.completed_at : sprint.started_at);
  const word = phase === 'closed' ? 'Closed' : 'Started';
  return day ? `${word} ${day}` : word;
}

// The sprint 'next' carry-over lands in, the same pick as the API: the lowest-numbered open sprint
// after this one. Null when there is none.
export function nextOpenSprint(sprints, sprint) {
  return (sprints || [])
    .filter(s => s.status !== 'closed' && s.number > sprint.number)
    .sort((a, b) => a.number - b.number)[0] || null;
}

// What completing a sprint holding `tasks` means, by this project's workflow roles. Canceled tasks
// are out of the sprint's scope; every other task is finished or carried over. The server decides
// the same way, so these counts match what the request moves.
export function carryOverSummary(tasks, statuses) {
  const names = flag => new Set((statuses || []).filter(s => s[flag]).map(s => s.name));
  const canceled = names('is_workflow_canceled');
  const complete = names('is_workflow_complete');
  const scope = (tasks || []).filter(t => !canceled.has(t.status));
  const unfinished = scope.filter(t => !complete.has(t.status));
  return {
    total: scope.length,
    finished: scope.length - unfinished.length,
    unfinished: unfinished.length,
    points: unfinished.reduce((sum, t) => sum + (t.story_points || 0), 0),
  };
}

// "1 item", "5 items", "4 unfinished items".
export function itemCount(count, adjective = '') {
  return `${count} ${adjective ? `${adjective} ` : ''}item${count === 1 ? '' : 's'}`;
}

// What a refused start or completion means to the user. Anything else keeps its own message.
export function lifecycleErrorText(err, noun = 'Sprint') {
  const lower = noun.toLowerCase();
  const code = err && err.body && err.body.error;
  if (code === 'sprint_closed') return `This ${lower} is already closed.`;
  if (code === 'sprint_already_started') return `This ${lower} has already started.`;
  if (code === 'no_next_sprint') return `There is no later open ${lower}. Choose another option.`;
  return (err && err.message) || `Failed to update this ${lower}.`;
}
